import { ResetPasswordCommand, UserProxy } from '@/app/proxy/proxies';
import { SharedModule } from '@/app/shared/shared.module';
import { Component, EventEmitter, Input, Output, OnInit, OnDestroy } from '@angular/core';
import { AuthService } from '../../../shared/services/auth.service';
import { PasswordService } from '@/app/auth/shared/services/password.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil, distinctUntilChanged, debounceTime } from 'rxjs/operators';
import { ToastService } from '@/app/core/services/toast.service';

@Component({
  selector: 'auth-change-pwd',
  imports: [SharedModule],
  providers: [UserProxy],
  templateUrl: './change-pwd.component.html',
  styleUrl: './change-pwd.component.less',
})
export class ChangePwdComponent implements OnInit, OnDestroy {
  @Output() steperChange = new EventEmitter<string>();
  @Input() loginInputType: 'email' | 'phone' = 'email';
  resetPwdModal: ResetPasswordCommand = new ResetPasswordCommand();

  confirmPassword = '';
  passwordFocused = false;

  // Reactive properties for password validation
  private passwordSubject = new BehaviorSubject<string>('');
  private destroy$ = new Subject<void>();

  isPasswordValid = false;
  passwordErrors: string[] = [];

  constructor(
    private _authService: AuthService,
    private _userProxy: UserProxy,
    private _passwordService: PasswordService,
    private _toastService: ToastService
  ) {
    this._authService.otpNumberForForgotPwd$.subscribe((otpNumber) => {
      if (otpNumber != null) {
        this.resetPwdModal.otp = parseInt(otpNumber);
      }
    });
    if (this.loginInputType === 'email') {
      this._authService.email$.subscribe((email) => {
        this.resetPwdModal.emailOrPhone = email || '';
      });
    } else {
      this._authService.phoneNumber$.subscribe((phoneNumber) => {
        this.resetPwdModal.emailOrPhone = phoneNumber || '';
      });
    }
  }

  ngOnInit() {
    // Set up reactive password validation
    this.passwordSubject
      .pipe(
        distinctUntilChanged(), // Only emit when password actually changes
        debounceTime(300), // Debounce to avoid too frequent validation
        takeUntil(this.destroy$)
      )
      .subscribe((password) => {
        // Only validate when password field has content
        if (password && password.trim().length > 0) {
          this.validatePassword(password);
        } else {
          // Reset validation state when field is empty
          this.isPasswordValid = false;
          this.passwordErrors = [];
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private validatePassword(password: string) {
    if (!password) {
      this.isPasswordValid = false;
      this.passwordErrors = [];
      return;
    }

    this.isPasswordValid = this._passwordService.checkPasswordIsValid(password);
    if (!this.isPasswordValid) {
      this.passwordErrors = this._passwordService.getPasswordValidationErrors(password);
    } else {
      this.passwordErrors = [];
    }
  }

  // Debug method you can call from browser console or component
  debugPassword(testPassword?: string) {
    const password = testPassword || this.resetPwdModal.newPassword || '';
    return {
      password: password,
      policy: this._passwordService.getCurrentPasswordPolicy(),
      detailedInfo: this._passwordService.getDetailedValidationInfo(password),
      isValid: this._passwordService.checkPasswordIsValid(password),
      errors: this._passwordService.getPasswordValidationErrors(password),
    };
  }

  // Method to be called when password changes in template
  onPasswordChange(password: string) {
    this.resetPwdModal.newPassword = password;
    this.passwordSubject.next(password);
  }

  backButton() {
    this.steperChange.emit('verify-otp');
  }

  saveNewPassword() {
    this._userProxy.user_ResetPassword(this.resetPwdModal).subscribe((res) => {
      if (res.isSuccess) {
        this.steperChange.emit('login');
        this._toastService.success();
      } else {
        this._toastService.error({ detail: res.errors?.[0]?.message });
      }
    });
  }

  get passwordValidationErrors(): string[] {
    return this.passwordErrors;
  }
}
